import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import PropTypes from "prop-types";

import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

// MUI Imports
import Grid from "@mui/material/Grid";

import ClearIcon from "@mui/icons-material/Clear";
import DateRangeIcon from "@mui/icons-material/DateRange";

import TableWithPaginationSorting from "../../../../../../Common/Table/TableWithPaginationSorting";
import KMKDateRangePicker from "../../../../../../Common/KMKDateRangePicker";

import WorkspaceService from "../../../../../../../service/workspace";

import { convertTimezone } from "../../../../../../../util/common";
import { Columns, rowsPerPageOptions, Job_Status_Types } from "../../../../utils/JobData";
import { dayjs } from "../../../../../../../util/dayjs";

const displayCustomCol = (JobType, row) => {
  let value = (JobType === 1 || JobType === 2 ? row?.Result?.message : row?.message) || "-";
  let color = ["failed", "error"].includes(row.Status?.toLowerCase())
    ? Job_Status_Types[row.Status?.toLowerCase()]?.color
    : "black";
  let showInfo = row.Result?.fileHistory && row.Result?.fileHistory?.length !== 0;
  let infoColor = color === "red" ? "error" : "info";
  let infoContent = row.Result?.fileHistory;
  let hideErrRecords = row.Result?.hideErrRecords || false;

  return { value, showInfo, color, infoColor, infoContent, hideErrRecords };
};

const JobHistoryList = (JobType, historyData) => {
  let historyDataArr = [];

  historyData?.forEach((row) => {
    const customColumn = displayCustomCol(JobType, row);

    historyDataArr.push([
      {
        RowId: row.JobHistory_id,
        value: row.StartTime ? convertTimezone(row.StartTime) : "-",
        align: "left",
      },
      {
        RowId: row.JobHistory_id,
        value: row.EndTime ? convertTimezone(row.EndTime) : "-",
        align: "left",
      },
      {
        RowId: row.JobHistory_id,
        value: Job_Status_Types[row.Status?.toLowerCase()]?.statusText ?? "-",
        align: "left",
        color: Job_Status_Types[row.Status?.toLowerCase()]?.color,
      },
      {
        RowId: row.JobHistory_id,
        message: JobType === 2 ? row?.Result?.message : row?.message || "",
        ...customColumn,
      },
    ]);
  });

  return historyDataArr;
};

const JOB_WITH_DETAILS = [1, 3, 4, 5, 6];

const History = (props) => {
  const { Workspace_id } = useParams();
  const { setHistoryDetails, resetHistoryDetails, selection, resetState } = props;

  const { data, id } = useSelector((state) => state.dataPipeline.editJob);
  const user = useSelector((state) => state.user);
  const { UserTimezone } = useMemo(() => user?.currentUser || {}, [user]);

  const [loading, setLoading] = useState(false);
  // later remove this and get it from redux
  const [isDVEnabled, setIsDVEnabled] = useState(true);
  const [historyList, setHistoryList] = useState([]);
  const [blockDetailedHistory, setBlockDetailedHistory] = useState(false);
  const [dateRange, setDateRange] = useState(null);
  const [options, setOptions] = useState({
    orderBy: "StartTime",
    order: "desc",
  });

  const Job_id = useMemo(() => parseInt(id), [id]);
  const { type: JobType } = useMemo(() => data, [data]);

  const optionsRef = useRef(options);

  const formatDate = useCallback((date, displayFormat) => {
    return displayFormat ? date.format("MM/DD/YYYY HH:mm:ss") : date.format("YYYY-MM-DD HH:mm:ss");
  }, []);

  const disabledDate = useCallback((current) => current && current > dayjs().endOf("day"), []);

  // Get History List
  const getHistoryList = useCallback(
    async (dates, tableOptions = optionsRef.current) => {
      setLoading(true);
      let startDate = null;
      let endDate = null;

      if (dates) {
        if (dates[0]) startDate = formatDate(dates[0]);
        if (dates[1]) endDate = formatDate(dates[1]);
      }

      const wsService = new WorkspaceService();
      const result = await wsService.getJobHistory(
        Workspace_id,
        Job_id,
        startDate,
        endDate,
        tableOptions,
        UserTimezone
      );

      if (result.success) {
        const { historyData, IsDataVaultEnabled } = result.data;
        setIsDVEnabled(IsDataVaultEnabled);

        const historyDataArr = JobHistoryList(JobType, historyData);
        setHistoryList(historyDataArr);
      }
      setLoading(false);
    },
    [Workspace_id, Job_id, UserTimezone, formatDate, JobType]
  );

  const getDetailedHistory = useCallback(
    (row, selectedRowID) =>
      setHistoryDetails((prev) => {
        if (prev.openJobHistory && prev.jobHistory_id === selectedRowID) return resetState;

        let dateRange = null;
        if (isDVEnabled) {
          let StartTime = row[0].value !== "-" ? new Date(row[0].value) : null;
          let EndTime = row[1].value !== "-" ? new Date(row[1].value) : null;
          dateRange = [StartTime, EndTime];
        }
        return {
          ...prev,
          jobHistory_id: selectedRowID,
          dateRange,
          openJobHistory: true,
          openFileHistory: false,
          openErrorRecords: false,
          selection: true,
          fileList: [],
        };
      }),
    [isDVEnabled, resetState, setHistoryDetails]
  );

  const handleFileDetails = useCallback(
    (row, selectedRowID, selectedCellID) =>
      setHistoryDetails((prev) => {
        if (prev.openFileHistory && prev.jobHistory_id === selectedRowID) return resetState;
        return {
          ...prev,
          openFileHistory: true,
          openJobHistory: false,
          openErrorRecords: false,
          jobHistory_id: selectedRowID,
          dateRange: null,
          selection: true,
          fileList: row[selectedCellID]?.infoContent || [],
          hideErrRecords: row[selectedCellID]?.hideErrRecords || false,
        };
      }),
    [resetState, setHistoryDetails]
  );

  const getDataUsingOptions = useCallback(
    (tableOptions) => {
      getHistoryList(dateRange, { ...tableOptions });
    },
    [dateRange, getHistoryList]
  );

  useEffect(() => {
    setBlockDetailedHistory(!JOB_WITH_DETAILS.includes(JobType));
    getHistoryList();
    return () => {};
  }, [JobType, getHistoryList]);

  return (
    <Grid container spacing={2}>
      <Grid
        size={{
          xs: 12,
          md: 7
        }}>
        <KMKDateRangePicker
          format={(value) => value.format("MM/DD/YYYY HH:mm:ss")}
          value={dateRange}
          onChange={(dates) => {
            setOptions({ ...options, page: 0 });
            setDateRange(dates);
            resetHistoryDetails();
            getHistoryList(dates, { ...options, page: 0 });
          }}
          disabledDate={disabledDate}
          size="middle"
          separator="~"
          allowClear={{ clearIcon: <ClearIcon sx={{ fontSize: "16px" }} /> }}
          suffixIcon={dateRange ? <ClearIcon sx={{ fontSize: "16px" }} /> : <DateRangeIcon sx={{ fontSize: "16px" }} />}
          variant="outlined"
          showTime
        />
      </Grid>
      <Grid size={12}>
        <TableWithPaginationSorting
          loading={loading}
          columns={Columns}
          tableData={historyList}
          ellipsisColumns={[4]}
          tableMaxWidth="912px"
          tableMaxHeight="100%"
          noDataColspan={5}
          noDataMessage="No records found"
          handleRequest={getDataUsingOptions}
          rowsPerPageOptions={rowsPerPageOptions}
          defaultOrder="desc"
          defaultOrderBy="StartTime"
          options={options}
          setOptions={setOptions}
          count={0}
          {...(!blockDetailedHistory && { RowClick: getDetailedHistory })}
          rowHover={!blockDetailedHistory}
          usePagination={false}
          selection={selection}
          handleInfo={handleFileDetails}
          showCopy={true}
        />
      </Grid>
    </Grid>
  );
};

History.propTypes = {
  selection: PropTypes.bool.isRequired,
  setHistoryDetails: PropTypes.func.isRequired,
  resetHistoryDetails: PropTypes.func.isRequired,
  resetState: PropTypes.object.isRequired,
};

export default History;
